import React, { useMemo, useState } from 'react';

// Class-level "Accuracy Over Time" explorer (ported 1:1 from the Personalized Plan,
// but plotting CLASS-AVERAGE % correct instead of a single student). Tree picker on
// the left (subject -> domain -> [Domain overall + skills]); multi-line chart on the
// right. Domain lines are solid; skill lines are dashed and only mark the tests where
// the skill appeared. Works for SAT / PSAT / ACT / SHSAT (sections come from the data).

type Bucket = { numCorrect: number; numQuestions: number };
type TestEntry = {
    testName: string;
    testDate: string;
    takenNum: number;
    avgCorrectPercent?: { [subject: string]: { [domain: string]: Bucket } };
    skillCorrectPercent?: { [subject: string]: { [domain: string]: { [skill: string]: Bucket } } };
};

const PALETTE = ['#0575a2', '#a23205', '#00a445', '#7c3aed', '#d97706', '#0ea5e9',
    '#db2777', '#65a30d', '#b3631f', '#0891b2', '#9333ea', '#16a34a'];

const displaySubject = (s: string) =>
    (s || '').replace('Reading and Writing', 'Reading & Writing')
        .replace('English Language Arts', 'ELA')
        .replace('ACT ', '').replace('Mathematics', 'Math');

const shortTestName = (name: string) =>
    (name || '').match(/Test\s*[A-Za-z]?\d+/)?.[0] || (name || '').replace('Digital ', '');

const pct = (b?: Bucket): number | null =>
    b && b.numQuestions > 0 ? b.numCorrect / b.numQuestions : null;

type DomLine = { subject: string; domain: string; color: string; values: (number | null)[]; acc: number | null };
type SkillLine = { subject: string; domain: string; skill: string; color: string; values: (number | null)[]; attempted: number; correct: number };

function AccuracyOverTime({ tests }: { tests: TestEntry[] }) {
    // oldest -> newest, completed tests only
    const taken = useMemo(() => (tests || []).filter(t => t.takenNum > 0).slice().reverse(), [tests]);
    const N = taken.length;

    const { domains, skillsByDom, subjects } = useMemo(() => {
        const subjects: string[] = [];
        const domOrder: { subject: string; domain: string }[] = [];
        const sklOrder: { subject: string; domain: string; skill: string }[] = [];
        taken.forEach(t => {
            Object.entries(t.avgCorrectPercent || {}).forEach(([subj, doms]) => {
                if (!subjects.includes(subj)) subjects.push(subj);
                Object.keys(doms).forEach(dom => {
                    if (!domOrder.some(d => d.subject === subj && d.domain === dom)) domOrder.push({ subject: subj, domain: dom });
                });
            });
            Object.entries(t.skillCorrectPercent || {}).forEach(([subj, doms]) => {
                Object.entries(doms).forEach(([dom, skills]) => {
                    Object.keys(skills).forEach(skill => {
                        if (!sklOrder.some(s => s.subject === subj && s.domain === dom && s.skill === skill))
                            sklOrder.push({ subject: subj, domain: dom, skill });
                    });
                });
            });
        });
        const colorOf: Record<string, string> = {};
        domOrder.forEach((d, i) => { colorOf[`${d.subject}::${d.domain}`] = PALETTE[i % PALETTE.length]; });

        const domains: DomLine[] = domOrder.map(d => {
            const values = taken.map(t => pct(t.avgCorrectPercent?.[d.subject]?.[d.domain]));
            let c = 0, q = 0;
            taken.forEach(t => { const b = t.avgCorrectPercent?.[d.subject]?.[d.domain]; if (b) { c += b.numCorrect; q += b.numQuestions; } });
            return { ...d, color: colorOf[`${d.subject}::${d.domain}`], values, acc: q > 0 ? c / q : null };
        });

        const skillsByDom: Record<string, SkillLine[]> = {};
        sklOrder.forEach(s => {
            const values = taken.map(t => pct(t.skillCorrectPercent?.[s.subject]?.[s.domain]?.[s.skill]));
            let c = 0, q = 0;
            taken.forEach(t => { const b = t.skillCorrectPercent?.[s.subject]?.[s.domain]?.[s.skill]; if (b) { c += b.numCorrect; q += b.numQuestions; } });
            const line: SkillLine = { ...s, color: colorOf[`${s.subject}::${s.domain}`], values, attempted: q, correct: c };
            (skillsByDom[`${s.subject}::${s.domain}`] = skillsByDom[`${s.subject}::${s.domain}`] || []).push(line);
        });
        return { domains, skillsByDom, subjects };
    }, [taken]);

    // Default: the 3 weakest domains (lowest class accuracy) shown as domain-overall lines.
    const defaultKeys = useMemo(() => domains
        .filter(d => d.acc != null)
        .sort((a, b) => (a.acc as number) - (b.acc as number))
        .slice(0, 3)
        .map(d => `dom::${d.subject}::${d.domain}`), [domains]);

    const [selected, setSelected] = useState<Set<string>>(() => new Set(defaultKeys));
    const [openDomains, setOpenDomains] = useState<Set<string>>(() => new Set(defaultKeys.map(k => k.split('::').slice(1).join('::'))));
    // keep defaults in sync if the test type / data changes
    const dkRef = React.useRef('');
    React.useEffect(() => {
        const sig = defaultKeys.join('|');
        if (sig !== dkRef.current) { dkRef.current = sig; setSelected(new Set(defaultKeys)); setOpenDomains(new Set(defaultKeys.map(k => k.split('::').slice(1).join('::')))); }
    }, [defaultKeys]);

    const toggleKey = (k: string) => setSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
    const toggleDomain = (dk: string) => setOpenDomains(prev => { const s = new Set(prev); s.has(dk) ? s.delete(dk) : s.add(dk); return s; });
    const selectAllInDomain = (subject: string, domain: string) => {
        const dk = `${subject}::${domain}`;
        const keys = [`dom::${dk}`, ...(skillsByDom[dk] || []).filter(s => s.attempted > 0).map(s => `skl::${dk}::${s.skill}`)];
        setSelected(prev => { const n = new Set(prev); const allOn = keys.every(k => n.has(k)); keys.forEach(k => allOn ? n.delete(k) : n.add(k)); return n; });
    };

    // Build the plotted trajectories from the current selection.
    const dashSeq = ['6,3', '2,3', '8,4,2,4'];
    type Traj = { key: string; label: string; sub: string; color: string; dash: string | null; w: number; points: { i: number; v: number }[] };
    const trajectories: Traj[] = [];
    const toPoints = (values: (number | null)[]) =>
        values.map((v, i) => (v == null ? null : { i, v })).filter(Boolean) as { i: number; v: number }[];
    domains.forEach(d => {
        const dk = `${d.subject}::${d.domain}`;
        if (selected.has(`dom::${dk}`)) trajectories.push({ key: `dom::${dk}`, label: d.domain, sub: `${displaySubject(d.subject)} · domain overall`, color: d.color, dash: null, w: 2.5, points: toPoints(d.values) });
        let di = 0;
        (skillsByDom[dk] || []).forEach(s => {
            if (selected.has(`skl::${dk}::${s.skill}`)) {
                trajectories.push({ key: `skl::${dk}::${s.skill}`, label: s.skill, sub: `${d.domain} · ${s.correct}/${s.attempted} correct`, color: d.color, dash: dashSeq[di % dashSeq.length], w: 2, points: toPoints(s.values) });
                di++;
            }
        });
    });
    // Plotted lines get distinct colors from a qualitative palette assigned in
    // SELECTION order (the Set preserves insertion order), so sibling skills and
    // their domain no longer share one base color — concurrent lines stay
    // distinguishable. A newly picked line takes the next palette color instead
    // of reshuffling existing lines. Domain lines stay solid, skill lines dashed.
    const LINE_PALETTE = [
        '#0575a2', '#a23205', '#6b21a8', '#0f7b6c', '#d34053',
        '#f2994a', '#2f80ed', '#7FB347', '#b8860b', '#e75480',
        '#1C2434', '#57cbf5',
    ];
    const selectionOrder = Array.from(selected);
    trajectories.forEach(t => {
        t.color = LINE_PALETTE[Math.max(0, selectionOrder.indexOf(t.key)) % LINE_PALETTE.length];
    });
    // Picker swatches mirror the plotted color for selected rows (gray when not
    // plotted) so the left panel always matches the chart.
    const plottedColor = new Map(trajectories.map(t => [t.key, t.color]));
    const UNPLOTTED = '#c8ccd2';

    // Zoom the x-axis to the span of tests the *plotted* lines actually cover,
    // so the chart fills its width instead of padding empty columns for the
    // earlier tests where none of the selected domains/skills were assessed
    // (different test forms cover different domains). Falls back to the full
    // range when nothing is selected.
    const plottedIdx = trajectories.flatMap(t => t.points.map(p => p.i));
    const firstI = plottedIdx.length ? Math.min(...plottedIdx) : 0;
    const lastI = plottedIdx.length ? Math.max(...plottedIdx) : Math.max(0, N - 1);
    const span = Math.max(1, lastI - firstI);
    const cols = lastI - firstI + 1;

    const PL = 44, PR = 20, PT = 18, PB = 50, H = 320;
    const W = Math.max(440, PL + PR + cols * 64);
    const iW = W - PL - PR, iH = H - PT - PB;
    const xF = (i: number) => PL + (iW / span) * (i - firstI);
    const yF = (v: number) => PT + (1 - v) * iH;
    const [hover, setHover] = useState<{ ti: number; pi: number } | null>(null);

    if (!N) {
        return (
            <div className="cdr__card">
                <div className="cdr__card-sub">Accuracy Over Time</div>
                <div className="cdr__empty" style={{ padding: '16px 0' }}>No graded results yet for this test type.</div>
            </div>
        );
    }

    return (
        <div className="cdr__card">
            <div className="cdr__card-head"><div>
                <div className="cdr__card-title">Accuracy Over Time</div>
                <div className="cdr__card-sub">
                    Pick any domains or skills to plot the class’s average % correct across all {N} test{N === 1 ? '' : 's'}.
                    Domain lines are solid; skill lines are dashed and only mark tests where the skill appeared.
                </div>
            </div></div>
            <div className="cdr__expwrap">
                {/* ── Picker ── */}
                <div className="cdr__exp-picker">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#676767', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Selected ({selected.size})</div>
                        <button onClick={() => setSelected(new Set())} disabled={selected.size === 0}
                            style={{ background: 'transparent', border: 'none', color: selected.size === 0 ? '#c8ccd2' : '#0575a2', fontSize: 11, fontWeight: 700, fontFamily: 'inherit', cursor: selected.size === 0 ? 'default' : 'pointer', padding: 0 }}>Clear</button>
                    </div>
                    {subjects.map(subj => (
                        <div key={subj} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#828282', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 4px 4px', borderBottom: '1px solid #EAECF0', marginBottom: 4 }}>{displaySubject(subj)}</div>
                            {domains.filter(d => d.subject === subj).map(d => {
                                const dk = `${d.subject}::${d.domain}`;
                                const skills = skillsByDom[dk] || [];
                                const selectableKeys = [`dom::${dk}`, ...skills.filter(s => s.attempted > 0).map(s => `skl::${dk}::${s.skill}`)];
                                const onCount = selectableKeys.filter(k => selected.has(k)).length;
                                const isOpen = openDomains.has(dk);
                                const allOn = onCount === selectableKeys.length && selectableKeys.length > 0;
                                return (
                                    <div key={dk} style={{ marginBottom: 2 }}>
                                        <button onClick={() => toggleDomain(dk)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', padding: '6px 4px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#676767" strokeWidth="3" style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .12s', flexShrink: 0 }}><polyline points="9,6 15,12 9,18" /></svg>
                                            <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#1C2434', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.domain}</span>
                                            <span style={{ fontSize: 10, color: '#828282', marginLeft: 'auto', flexShrink: 0 }}>{onCount}/{selectableKeys.length}</span>
                                        </button>
                                        {isOpen && (
                                            <div style={{ paddingLeft: 22, paddingBottom: 4 }}>
                                                {selectableKeys.length > 1 && (
                                                    <button onClick={() => selectAllInDomain(d.subject, d.domain)} style={{ background: 'transparent', border: 'none', color: '#0575a2', fontSize: 10, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', padding: '2px 4px', marginBottom: 2, display: 'block' }}>{allOn ? 'Deselect all' : 'Select all in domain'}</button>
                                                )}
                                                {[{ k: `dom::${dk}`, label: 'Domain overall', dash: false, disabled: false, title: '' },
                                                ...skills.map(s => ({ k: `skl::${dk}::${s.skill}`, label: s.skill, dash: true, disabled: s.attempted === 0, title: `${s.correct}/${s.attempted} correct` }))
                                                ].map(row => {
                                                    const on = selected.has(row.k);
                                                    const lineColor = on ? (plottedColor.get(row.k) || d.color) : UNPLOTTED;
                                                    return (
                                                        <label key={row.k} title={row.title} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px', borderRadius: 4, cursor: row.disabled ? 'default' : 'pointer', opacity: row.disabled ? 0.45 : 1, background: on ? '#fff' : 'transparent', border: `1px solid ${on ? '#EAECF0' : 'transparent'}` }}>
                                                            <input type="checkbox" checked={on} disabled={row.disabled} onChange={() => toggleKey(row.k)} style={{ accentColor: lineColor, margin: 0 }} />
                                                            <span style={{ width: 12, height: 0, flexShrink: 0, borderTop: `2.5px ${row.dash ? 'dashed' : 'solid'} ${lineColor}` }} />
                                                            {/* paddingRight keeps the italic overhang from being clipped by overflow:hidden */}
                                                            <span style={{ fontSize: 12, color: '#1C2434', fontStyle: row.dash ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 4 }}>{row.label}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* ── Chart ── */}
                <div style={{ minWidth: 0 }}>
                    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
                            {[0, 25, 50, 75, 100].map(g => (
                                <g key={g}>
                                    <line x1={PL} x2={W - PR} y1={yF(g / 100)} y2={yF(g / 100)} stroke={g === 0 ? '#EAECF0' : '#f3f3f3'} />
                                    <text x={PL - 8} y={yF(g / 100) + 4} textAnchor="end" fontSize="11" fill="#828282">{g}%</text>
                                </g>
                            ))}
                            {trajectories.length === 0 && (
                                <text x={PL + iW / 2} y={PT + iH / 2} textAnchor="middle" fontSize="13" fill="#828282">Pick a domain or skill on the left to plot it.</text>
                            )}
                            {trajectories.map((t, ti) => (
                                <g key={t.key}>
                                    {t.points.length > 1 && (
                                        <polyline points={t.points.map(p => `${xF(p.i)},${yF(p.v)}`).join(' ')} fill="none" stroke={t.color} strokeWidth={t.w} strokeDasharray={t.dash || undefined} strokeLinecap="round" strokeLinejoin="round" />
                                    )}
                                    {t.points.map((p, pi) => (
                                        <circle key={pi} cx={xF(p.i)} cy={yF(p.v)} r={hover && hover.ti === ti && hover.pi === pi ? 6 : 3.5} fill={t.color} stroke="#fff" strokeWidth="1.5"
                                            onMouseEnter={() => setHover({ ti, pi })} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }} />
                                    ))}
                                </g>
                            ))}
                            {taken.map((t, i) => (i < firstI || i > lastI) ? null : (
                                <g key={i} transform={`translate(${xF(i)},${H - PB + 14})`}>
                                    <text textAnchor="end" fontSize="10" fill="#828282" transform="rotate(-35)">{shortTestName(t.testName)}</text>
                                </g>
                            ))}
                            {hover && trajectories[hover.ti] && (() => {
                                // Compact tooltip matching the Personalized Plan explorer:
                                // topic name + test name + "% · date". Nothing else.
                                const t = trajectories[hover.ti];
                                const p = t.points[hover.pi];
                                const x = xF(p.i), y = yF(p.v);
                                const name = t.label.length > 28 ? `${t.label.slice(0, 27)}…` : t.label;
                                const testName = (taken[p.i].testName || '').replace(/^Digital /, '');
                                const date = taken[p.i].testDate
                                    ? new Date(taken[p.i].testDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                    : '—';
                                const line3 = `${Math.round(p.v * 100)}% · ${date}`;
                                const bw = Math.max(name.length * 6.4, testName.length * 6.0, line3.length * 6.0) + 20;
                                const tx = x > W * 0.6 ? x - bw - 10 : x + 10;
                                return (
                                    <g pointerEvents="none">
                                        <rect x={tx} y={y - 61} width={bw} height="53" rx="8" fill="#1C2434" />
                                        <text x={tx + 10} y={y - 46} fontSize="11" fontWeight="700" fill="#fff">{name}</text>
                                        <text x={tx + 10} y={y - 31} fontSize="11" fill="#cdd5e0">{testName}</text>
                                        <text x={tx + 10} y={y - 16} fontSize="11" fill="#aadddd">{line3}</text>
                                    </g>
                                );
                            })()}
                        </svg>
                    </div>
                    {trajectories.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                            {trajectories.map(t => (
                                <span key={t.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 8px', background: '#fff', border: '1px solid #EAECF0', borderRadius: 40, fontSize: 12, color: '#1C2434' }}>
                                    <span style={{ width: 18, height: 0, borderTop: `2.5px ${t.dash ? 'dashed' : 'solid'} ${t.color}`, display: 'inline-block' }} />
                                    <span style={{ fontWeight: 600 }}>{t.label}</span>
                                    <button onClick={() => toggleKey(t.key)} title="Remove" style={{ background: 'transparent', border: 'none', color: '#828282', cursor: 'pointer', padding: 0, marginLeft: 2, display: 'inline-flex' }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AccuracyOverTime;
